import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setDiffsList, setListStatistics, setNavigationList } from '@/store/diffsSlice';
import { supabase } from '@/integrations/supabase/client';
import type { RootState } from '@/store/store';
import { isYearPeriod, parseYearPeriod } from '@/utils/periodUtils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Building2, Tag, List, X, Search, Download } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { DiffCard } from './DiffCard';
import { DiffEventLightbox } from './DiffEventLightbox';
import { useToast } from "@/hooks/use-toast";
import { useFixDiffTasks } from '@/hooks/useFixDiffTasks';
import { exportEventsToTSV } from '@/utils/csvExportUtils';

// Helper function to parse PostgreSQL array string format to JavaScript array
const parsePostgresArray = (value: any): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;

  // PostgreSQL array format: "{item1,item2,item3}"
  if (typeof value === 'string' && value.startsWith('{') && value.endsWith('}')) {
    const content = value.slice(1, -1);
    if (!content) return [];
    return content.split(',').map(item => item.trim());
  }

  return [];
};

// Helper function to format category names for display
const formatCategoryLabel = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'null';

  // Special cases
  if (category === 'pricing_structure') return 'Pricing Structure';

  // General formatting: replace underscores with spaces and capitalize each word
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Helper function to get category sort priority
const getCategorySortPriority = (category: string): number => {
  const priorityOrder: Record<string, number> = {
    'pricing_structure': 1,
    'feature': 2,
    'limits': 3,
    'misc': 4,
  };
  return priorityOrder[category.toLowerCase()] || 999;
};

// Helper function to get unique color for each category
const getCategoryColor = (category: string | null | undefined): string => {
  if (!category || category === 'null') return 'bg-red-100 text-red-800 border-red-300';

  const colorMap: Record<string, string> = {
    'pricing_structure': 'bg-purple-100 text-purple-800 border-purple-300',
    'feature': 'bg-blue-100 text-blue-800 border-blue-300',
    'limits': 'bg-orange-100 text-orange-800 border-orange-300',
    'misc': 'bg-gray-100 text-gray-800 border-gray-300',
    'pricing': 'bg-green-100 text-green-800 border-green-300',
    'metric': 'bg-teal-100 text-teal-800 border-teal-300',
    'discount': 'bg-pink-100 text-pink-800 border-pink-300',
    'plan': 'bg-indigo-100 text-indigo-800 border-indigo-300',
  };

  return colorMap[category.toLowerCase()] || 'bg-slate-100 text-slate-800 border-slate-300';
};

interface Diff {
  id: string;
  page_id: string;
  slug: string;
  v_before: string;
  v_after: string;
  asset_id?: string;
  hook?: string;
  description?: string;
  published?: boolean;
  created_at?: string;
  updated_at?: string;
  period?: string;
  delta?: number;
  primary_cropped_url?: string;
  secondary_cropped_url?: string;
  primary_original_url?: string;
  secondary_original_url?: string;
  page?: {
    id: string;
    subslug?: string;
    pricing_page_url?: string;
    company?: {
      id: string;
      slug: string;
      url?: string;
      logo_url?: string;
    };
  };
  changes: Array<{
    id: string;
    event_type?: string;
    category?: string;
    slug: string;
    v_before?: string;
    v_after?: string;
    description?: string;
    before_extracts?: any;
    after_extract?: any;
    verified?: boolean;
    partially_correct?: boolean;
    asset_id?: string;
    tags?: string[];
    asset_ids?: string[];
    created_at?: string;
    updated_at?: string;
  }>;
  changesSummary?: Array<{
    id: string;
    event_type?: string;
    category?: string;
    verified?: boolean;
    partially_correct?: boolean;
    invalid?: boolean;
  }>;
}

interface DiffsListTabProps {
  periodFilter: string;
  onPeriodChange?: (period: string) => void;
  publishedFilter?: string;
  onPublishedFilterChange?: (value: string) => void;
}

export const DiffsListTab: React.FC<DiffsListTabProps> = ({ periodFilter, onPeriodChange, publishedFilter: publishedFilterProp, onPublishedFilterChange }) => {
  const dispatch = useDispatch();
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Get cached data from Redux
  const cachedList = useSelector((state: RootState) => state.diffs.listCache);
  const cachedStats = useSelector((state: RootState) => state.diffs.listStatistics);

  // Fetch active fix_diff tasks
  const { tasksByNewsId, refetch: refetchFixDiffTasks } = useFixDiffTasks();

  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(false);

  // Advanced filters - collapsed by default
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [createdFilter, setCreatedFilter] = useState<string>(searchParams.get('created') || 'all');
  const [publishedFilterInternal, setPublishedFilterInternal] = useState<string>(searchParams.get('published') || 'all');
  const publishedFilter = publishedFilterProp !== undefined ? publishedFilterProp : publishedFilterInternal;
  const setPublishedFilter = (value: string) => {
    if (onPublishedFilterChange) {
      onPublishedFilterChange(value);
    } else {
      setPublishedFilterInternal(value);
    }
  };
  const [slugInput, setSlugInput] = useState<string>(searchParams.get('slug') || '');
  const [activeSlugFilter, setActiveSlugFilter] = useState<string>(searchParams.get('slug') || '');

  // View mode: company (default), event
  const [viewMode, setViewMode] = useState<'company' | 'event'>((searchParams.get('view') as 'company' | 'event') || 'company');

  // Event view sort: type (default), company, or created
  const [eventSort, setEventSort] = useState<'type' | 'company' | 'created'>((searchParams.get('sort') as 'type' | 'company' | 'created') || 'type');

  // Diff view sort: updated (default), created, company, or version
  const [diffSort, setDiffSort] = useState<'company' | 'created' | 'updated' | 'version'>((searchParams.get('diffSort') as 'company' | 'created' | 'updated' | 'version') || 'updated');

  // Event type filter - empty means show all
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>(() => {
    const typesParam = searchParams.get('types');
    return typesParam ? typesParam.split(',') : [];
  });

  // Tag filter - empty means show all
  const [selectedTags, setSelectedTags] = useState<string[]>(() => {
    const tagsParam = searchParams.get('tags');
    return tagsParam ? tagsParam.split(',') : [];
  });

  // Pagination state - load events incrementally
  const [hasMoreEvents, setHasMoreEvents] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const PAGE_SIZE = 50; // Load 50 events at a time

  // Track previous period filter to detect actual changes
  const prevPeriodFilter = useRef(periodFilter);
  const prevEventSort = useRef(eventSort);
  const prevDiffSort = useRef(diffSort);

  // Use cached data or local state
  const diffs = cachedList?.data.diffs || [];
  const totalCount = cachedList?.data.totalCount || 0;
  const stats = cachedStats?.data || null;

  // Server-side tag counts (fetched separately from main data)
  const [serverTagCounts, setServerTagCounts] = useState<Array<[string, number]>>([]);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxContext, setLightboxContext] = useState<{
    diffId: string;
    eventId?: string;
    companySlug: string;
    events: any[];
  } | null>(null);

  const createdRangeOptions = [
    { value: '7', label: 'Last 7 days' },
    { value: '30', label: 'Last 30 days' },
    { value: '90', label: 'Last 90 days' },
    { value: 'all', label: 'All time' }
  ];

  const publishedStatuses = [
    { value: 'all', label: 'All' },
    { value: 'published', label: 'Published' },
    { value: 'unpublished', label: 'Unpublished' }
  ];

  // Helper to update URL params
  const updateUrlParams = (updates: Record<string, string | null>) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === '' || value === 'all') {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  };

  const handleSearch = () => {
    const newSlug = slugInput.trim();
    setActiveSlugFilter(newSlug);
    updateUrlParams({ slug: newSlug || null });
    // Reset pagination and reload with new filter
    // Pass the slug directly to avoid stale state
    loadDiffs(1, true, false, newSlug);
  };

  const toggleEventType = (eventType: string) => {
    setSelectedEventTypes(prev => {
      const newTypes = prev.includes(eventType)
        ? prev.filter(t => t !== eventType)
        : [...prev, eventType];

      // Update URL
      updateUrlParams({ types: newTypes.length > 0 ? newTypes.join(',') : null });

      return newTypes;
    });
  };

  const toggleTag = (tag: string) => {
    setSelectedTags(prev => {
      const newTags = prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag];

      // Update URL
      updateUrlParams({ tags: newTags.length > 0 ? newTags.join(',') : null });

      return newTags;
    });
  };

  // Fetch tag counts separately - extracted to allow re-fetching when using cached data
  const fetchTagCounts = async () => {
    try {
      // Check if year period filter - need to use RPC or manual filtering
      if (periodFilter && isYearPeriod(periodFilter)) {
        const parsed = parseYearPeriod(periodFilter);
        if (parsed) {
          // For year filters, we need to get tags from year-over-year diffs
          // Since there's no direct RPC for tags, fetch the diffs and extract tags
          const { data, error } = await supabase.rpc('get_year_over_year_diffs', {
            target_year: parsed.year.toString(),
            slug_filter: null,
            tags_filter: null,
            limit_count: 1000,  // Get more to count all tags
            offset_count: 0
          });

          if (error) {
            console.error('[DiffsListTab] Tag counts error (year filter):', error);
            return;
          }

          // Aggregate tags client-side
          const tagMap: Record<string, number> = {};
          (data || []).forEach((row: any) => {
            const tags = parsePostgresArray(row.tags);
            tags.forEach((tag: string) => {
              tagMap[tag] = (tagMap[tag] || 0) + 1;
            });
          });

          // Sort by count and take top 25
          const sortedTags = Object.entries(tagMap)
            .sort(([, a], [, b]) => b - a)
            .slice(0, 25);

          setServerTagCounts(sortedTags);
          return;
        }
      }

      // Regular period filter or no filter
      let query = supabase
        .from('app_news_with_events')
        .select('tags')
        .not('tags', 'is', null);

      if (periodFilter) {
        query = query.eq('period', periodFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[DiffsListTab] Tag counts error:', error);
        return;
      }

      // Aggregate tags client-side
      const tagMap: Record<string, number> = {};
      (data || []).forEach((row: any) => {
        const tags = parsePostgresArray(row.tags);
        tags.forEach((tag: string) => {
          tagMap[tag] = (tagMap[tag] || 0) + 1;
        });
      });

      // Sort by count and take top 25
      const sortedTags = Object.entries(tagMap)
        .sort(([, a], [, b]) => b - a)
        .slice(0, 25);

      setServerTagCounts(sortedTags);
    } catch (error) {
      console.error('[DiffsListTab] Error fetching tag counts:', error);
    }
  };

  const loadDiffs = async (page: number = 1, forceRefresh: boolean = false, loadMore: boolean = false, slugOverride?: string) => {
    try {
      const cacheKey = periodFilter || 'all';
      const isCacheValid = cachedList?.periodFilter === cacheKey && !forceRefresh && !loadMore;

      if (isCacheValid && !loadMore) {
        setLoading(false);
        setStatsLoading(false);

        // Even with valid cache, fetch tag counts if they're empty (component state isn't cached)
        if (serverTagCounts.length === 0) {
          fetchTagCounts();
        }
        return;
      }

      // If loading more, use different loading state
      if (loadMore) {
        setLoadingMore(true);
      } else {
        console.log('[DiffsListTab] Fetching diffs with pagination from app_news_with_events for period:', periodFilter);
        setLoading(true);
        setCurrentOffset(0);
        setHasMoreEvents(true);
      }

      const diffsStart = Date.now();

      // OPTIMIZATION: Fetch statistics using SQL COUNT in parallel with data loading
      // Only fetch stats on initial load, not when loading more
      const statsPromise = !loadMore ? (async () => {
        try {
          setStatsLoading(true);

          let categoryCounts, uniqueCounts;

          // Check if year period filter - use year-specific RPC functions
          if (periodFilter && isYearPeriod(periodFilter)) {
            const parsed = parseYearPeriod(periodFilter);
            if (parsed) {
              // Use year-over-year comparison statistics functions
              [categoryCounts, uniqueCounts] = await Promise.all([
                supabase.rpc('get_year_over_year_category_counts', {
                  target_year: parsed.year.toString()
                }),
                supabase.rpc('get_year_over_year_unique_counts', {
                  target_year: parsed.year.toString()
                })
              ]);
            }
          } else {
            // Use regular statistics functions for week/quarter periods
            [categoryCounts, uniqueCounts] = await Promise.all([
              supabase.rpc('get_category_counts', { period_filter: periodFilter || null }),
              supabase.rpc('get_unique_counts', { period_filter: periodFilter || null })
            ]);
          }

          if (categoryCounts?.error) {
            console.error('[DiffsListTab] Category counts error:', categoryCounts.error);
            throw categoryCounts.error;
          }

          if (uniqueCounts?.error) {
            console.error('[DiffsListTab] Unique counts error:', uniqueCounts.error);
            throw uniqueCounts.error;
          }

          // Parse category counts from RPC result
          const eventTypeCounts: Record<string, number> = {};
          (categoryCounts?.data || []).forEach((row: any) => {
            eventTypeCounts[row.category] = parseInt(row.count);
          });

          // Get unique counts from RPC result
          // Note: Year-over-year RPC returns { news_count, company_count, event_count }
          // Regular RPC returns { total_diffs, unique_companies, total_events }
          const countsData = uniqueCounts?.data?.[0];
          const counts = {
            total_diffs: countsData?.news_count || countsData?.total_diffs || 0,
            unique_companies: countsData?.company_count || countsData?.unique_companies || 0,
            total_events: countsData?.event_count || countsData?.total_events || 0
          };

          setStatsLoading(false);

          return {
            totalDiffs: parseInt(counts.total_diffs),
            uniqueCompanies: parseInt(counts.unique_companies),
            totalChanges: parseInt(counts.total_events),
            eventTypeCounts
          };
        } catch (error) {
          console.error('[DiffsListTab] Error fetching statistics:', error);
          setStatsLoading(false);
          return null;
        }
      })() : Promise.resolve(null);

      // Fetch tag counts separately (server-side aggregation)
      // Only fetch on initial load, not when loading more
      if (!loadMore) {
        fetchTagCounts();
      }

      // Build query to app_news_with_events view
      // NOTE: The view returns one row per event
      // Fetch records using pagination - only load PAGE_SIZE events at a time
      const offset = loadMore ? currentOffset : 0;

      // Use slugOverride if provided (for immediate search), otherwise use activeSlugFilter
      const effectiveSlug = slugOverride !== undefined ? slugOverride : activeSlugFilter;

      let data, error;

      // Check if year period filter - use RPC function for year-over-year comparison
      if (periodFilter && isYearPeriod(periodFilter)) {
        const parsed = parseYearPeriod(periodFilter);
        if (parsed) {
          // Call RPC function for year-over-year comparison
          const result = await supabase.rpc('get_year_over_year_diffs', {
            target_year: parsed.year.toString(),
            slug_filter: effectiveSlug.trim() || null,
            tags_filter: selectedTags.length > 0 ? selectedTags : null,
            limit_count: PAGE_SIZE,
            offset_count: offset
          });
          data = result.data;
          error = result.error;
        }
      } else {
        // Regular query for week/quarter periods or no filter
        let query = supabase
          .from('app_news_with_events')
          .select('*')
          .order('created_at', { ascending: false })
          .range(offset, offset + PAGE_SIZE - 1);

        // Apply company slug filter if provided (database-level filtering)
        if (effectiveSlug.trim() !== '') {
          query = query.ilike('slug', `%${effectiveSlug}%`);
        }

        // Apply period filter if provided
        if (periodFilter) {
          query = query.eq('period', periodFilter);
        }

        // Apply tag filter if provided (server-side filtering)
        // Use overlaps operator to check if tags array contains any of the selected tags
        if (selectedTags.length > 0) {
          query = query.overlaps('tags', selectedTags);
        }

        // Execute query
        const result = await query;
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('[DiffsListTab] Error fetching diffs:', error);
        throw new Error(`Failed to fetch diffs: ${error.message}`);
      }

      // Check if there are more events to load
      const hasMore = data && data.length === PAGE_SIZE;
      setHasMoreEvents(hasMore);
      setCurrentOffset(offset + PAGE_SIZE);

      // Transform flat event rows into grouped diff objects
      // Group events by news_id
      // When loading more, deep clone existing diffs to avoid mutation issues
      const diffsMap = loadMore
        ? new Map(diffs.map(d => [d.id, { ...d, changes: [...d.changes] }]))
        : new Map<string, any>();

      (data || []).forEach((row: any) => {
        // Use news_id as primary key, fall back to composite key
        const diffKey = row.news_id || `${row.slug}_${row.v_before}_${row.v_after}`;

        if (!diffsMap.has(diffKey)) {
          // Create new diff object with parent news data
          diffsMap.set(diffKey, {
            id: row.news_id,
            page_id: row.page_id,
            slug: row.slug,
            v_before: row.v_before,
            v_after: row.v_after,
            hook: row.hook,
            description: row.news_description,
            published: row.published,
            created_at: row.created_at,
            updated_at: row.updated_at,
            period: row.period,
            delta: row.delta,
            company_logo_url: row.logo_url,
            changes: [],
          });
        }

        // Add this event to the diff's changes array
        const diff = diffsMap.get(diffKey);
        diff.changes.push({
          id: row.id,
          event_type: row.event_type,
          category: row.category,
          description: row.description,
          slug: row.slug,
          v_before: row.v_before,
          v_after: row.v_after,
          before_extracts: row.before_extracts,
          after_extract: row.after_extract,
          verified: row.verified,
          partially_correct: row.partially_correct,
          invalid: row.invalid,
          asset_id: row.asset_id,
          tags: parsePostgresArray(row.tags),
          asset_ids: Array.isArray(row.asset_ids) ? row.asset_ids : [],
          labels: parsePostgresArray(row.labels),
          annotation_coordinates: row.annotation_coordinates,
          primary_cropped_url: row.primary_cropped_url,
          secondary_cropped_url: row.secondary_cropped_url,
          primary_original_url: row.primary_original_url,
          secondary_original_url: row.secondary_original_url,
          created_at: row.created_at,
          updated_at: row.updated_at,
        });
      });

      const finalDiffs = Array.from(diffsMap.values());

      // Wait for statistics to complete (only on initial load)
      const statsData = await statsPromise;

      // Cache everything in Redux
      dispatch(setDiffsList({
        diffs: finalDiffs,
        totalCount: finalDiffs.length,
        currentPage: 1,
        hasNextPage: hasMore,
        hasPrevPage: false,
        periodFilter: cacheKey,
      }));

      if (statsData) {
        // Use SQL-based counts
        dispatch(setListStatistics({
          statistics: statsData,
          periodFilter: cacheKey,
        }));
      }

      setLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error('Error loading diffs:', err);
      toast({
        title: "Error",
        description: "Failed to load diffs",
        variant: "destructive",
      });
      setLoading(false);
      setLoadingMore(false);
      setStatsLoading(false);
    }
  };

  const loadMoreEvents = () => {
    if (!loadingMore && hasMoreEvents) {
      loadDiffs(1, false, true);
    }
  };

  // Category priority for sorting (same as DiffCard)
  const categoryPriority: Record<string, number> = {
    'pricing_structure': 1,
    'feature': 2,
    'limits': 3,
    'misc': 4,
    'null': 5,
  };

  const handleOpenLightbox = (diffId: string, eventId: string | undefined, companySlug: string, passedEvents?: any[]) => {
    // Use passed events if provided (already enriched and sorted from event view)
    // Otherwise, create enriched events from filtered diffs
    let enrichedEvents: any[];

    if (passedEvents && passedEvents.length > 0) {
      // Events already enriched and in correct order from caller
      // Just apply category filter if needed
      enrichedEvents = selectedEventTypes.length > 0
        ? passedEvents.filter(event => {
            const category = event.category ?? 'null';
            return selectedEventTypes.includes(category);
          })
        : passedEvents;
    } else {
      // Flatten all events from all filtered diffs
      // Sort events WITHIN each diff by category priority (same order as DiffCard)
      // This keeps diffs grouped together while sorting categories within each diff
      enrichedEvents = filteredDiffs.flatMap(diff => {
        const filteredEvents = (diff.changes || [])
          .filter(event => {
            // Apply category filter
            if (selectedEventTypes.length === 0) return true;
            const category = event.category ?? 'null';
            return selectedEventTypes.includes(category);
          })
          .map(event => ({
            ...event,
            // v_before/v_after already on event from view
            // image URLs already on event from view (from event's asset_id)
            // Add company logo from parent diff
            logo_url: diff.company_logo_url,
          }));

        // Sort this diff's events by category priority (features before misc)
        return filteredEvents.sort((a, b) => {
          const aCat = a.category ?? 'null';
          const bCat = b.category ?? 'null';
          const aPriority = categoryPriority[aCat] ?? 999;
          const bPriority = categoryPriority[bCat] ?? 999;
          return aPriority - bPriority;
        });
      });
    }

    setLightboxContext({ diffId, eventId, companySlug, events: enrichedEvents });
    setLightboxOpen(true);

    // Update URL with eventId for deep linking
    if (eventId) {
      updateUrlParams({ eventId });
    }
  };

  const handleExportCSV = async () => {
    try {
      toast({
        title: "Exporting...",
        description: "Fetching all events from database...",
      });

      // Fetch ALL events from database with current filters (no pagination)
      let data, error;

      // Check if year period filter - use RPC function for year-over-year comparison
      if (periodFilter && isYearPeriod(periodFilter)) {
        const parsed = parseYearPeriod(periodFilter);
        if (parsed) {
          // Call RPC function for year-over-year comparison (no limit)
          const result = await supabase.rpc('get_year_over_year_diffs', {
            target_year: parsed.year.toString(),
            slug_filter: activeSlugFilter.trim() || null,
            tags_filter: selectedTags.length > 0 ? selectedTags : null,
            limit_count: 100000, // Large limit to get all events
            offset_count: 0
          });
          data = result.data;
          error = result.error;
        }
      } else {
        // Regular query for week/quarter periods or no filter
        let query = supabase
          .from('app_news_with_events')
          .select('*')
          .order('created_at', { ascending: false });

        // Apply company slug filter if provided
        if (activeSlugFilter.trim() !== '') {
          query = query.ilike('slug', `%${activeSlugFilter}%`);
        }

        // Apply period filter if provided
        if (periodFilter) {
          query = query.eq('period', periodFilter);
        }

        // Apply tag filter if provided
        if (selectedTags.length > 0) {
          query = query.overlaps('tags', selectedTags);
        }

        // Execute query (no limit)
        const result = await query;
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('[DiffsListTab] Error fetching events for export:', error);
        throw new Error(`Failed to fetch events: ${error.message}`);
      }

      // Transform flat event rows and apply filters
      const eventsToExport = (data || [])
        .filter((row: any) => {
          // Apply category filter
          if (selectedEventTypes.length === 0) return true;
          const category = row.category ?? 'null';
          return selectedEventTypes.includes(category);
        })
        .filter((row: any) => {
          // Apply published filter
          if (publishedFilter === 'published') return row.published === true;
          if (publishedFilter === 'unpublished') return row.published !== true;
          return true;
        })
        .filter((row: any) => {
          // Apply created_after filter
          if (createdFilter === 'all') return true;
          const daysAgo = parseInt(createdFilter);
          const date = new Date();
          date.setDate(date.getDate() - daysAgo);
          const cutoff = date.toISOString();
          return row.created_at && row.created_at >= cutoff;
        })
        .map((row: any) => ({
          slug: row.slug,
          v_before: row.v_before,
          v_after: row.v_after,
          description: row.description,
          annotation_coordinates: row.annotation_coordinates,
          primary_original_url: row.primary_original_url,
          secondary_original_url: row.secondary_original_url,
          tags: parsePostgresArray(row.tags),
        }));

      if (eventsToExport.length === 0) {
        toast({
          title: "No events to export",
          description: "There are no events matching the current filters.",
          variant: "destructive",
        });
        return;
      }

      // Export to TSV
      const stats = exportEventsToTSV(eventsToExport, periodFilter);

      // Show detailed toast with stats
      const missingCount = stats.total - stats.withUrls;
      let description = `Exported ${stats.total} events to TSV.`;

      if (missingCount > 0) {
        description += ` ${missingCount} events are missing viewer URLs (no annotation data).`;
      } else {
        description += ` All events have viewer URLs.`;
      }

      toast({
        title: "Export successful",
        description,
      });
    } catch (err) {
      console.error('Error exporting TSV:', err);
      toast({
        title: "Export failed",
        description: "Failed to export events to TSV.",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    loadDiffs(1);
  }, []);

  useEffect(() => {
    // Only reset filters when period actually changes (not on initial mount)
    if (prevPeriodFilter.current !== periodFilter) {
      setSelectedEventTypes([]);
      updateUrlParams({ types: null });
      // Re-run search with new period filter (respects active company filter)
      loadDiffs(1, true);
      prevPeriodFilter.current = periodFilter;
    }
  }, [periodFilter]);

  // Reset pagination and reload when sort changes
  useEffect(() => {
    // Only reload when sort actually changes (not on initial mount)
    if (prevEventSort.current !== eventSort || prevDiffSort.current !== diffSort) {
      loadDiffs(1, true);
      prevEventSort.current = eventSort;
      prevDiffSort.current = diffSort;
    }
  }, [eventSort, diffSort]);

  // Track previous tags to detect changes
  const prevSelectedTags = useRef(selectedTags);

  // Reload when tag filter changes (server-side filtering)
  useEffect(() => {
    // Only reload when tags actually change (not on initial mount)
    const tagsChanged = JSON.stringify(prevSelectedTags.current) !== JSON.stringify(selectedTags);
    if (tagsChanged) {
      loadDiffs(1, true);
      prevSelectedTags.current = selectedTags;
    }
  }, [selectedTags]);

  // Deep linking: Check for eventId in URL and open lightbox
  useEffect(() => {
    const eventIdParam = searchParams.get('eventId');

    if (eventIdParam && !loading && diffs.length > 0) {
      // Find the event in the loaded diffs
      const findEventInDiffs = () => {
        for (const diff of diffs) {
          const event = diff.changes?.find((e: any) => e.id === eventIdParam);
          if (event) {
            return {
              event,
              diff,
              companySlug: diff.slug
            };
          }
        }
        return null;
      };

      const result = findEventInDiffs();

      if (result) {
        // Open lightbox with the found event
        handleOpenLightbox(
          result.diff.id,
          result.event.id,
          result.companySlug,
          undefined // Let handleOpenLightbox gather all events
        );
      } else {
        // Event not found in current loaded diffs
        // This could happen if the event is from a different period
        console.warn('[DiffsListTab] Event not found in loaded diffs:', eventIdParam);
        toast({
          title: "Event not found",
          description: "The event may be from a different time period or may not exist.",
          variant: "destructive",
        });
        // Clear the invalid eventId from URL
        updateUrlParams({ eventId: null });
      }
    }
  }, [searchParams, loading, diffs]);

  // Client-side filtering
  const filteredDiffs = React.useMemo(() => {
    let filtered = [...diffs];

    // Note: Slug filter is now applied at database level, not here

    // Apply published filter
    if (publishedFilter === 'published') {
      filtered = filtered.filter(d => d.published === true);
    } else if (publishedFilter === 'unpublished') {
      filtered = filtered.filter(d => d.published !== true);
    }

    // Apply created_after filter
    if (createdFilter !== 'all') {
      const daysAgo = parseInt(createdFilter);
      const date = new Date();
      date.setDate(date.getDate() - daysAgo);
      const cutoff = date.toISOString();
      filtered = filtered.filter(d => d.created_at && d.created_at >= cutoff);
    }

    // Apply category filter
    if (selectedEventTypes.length > 0) {
      filtered = filtered.filter(d => {
        const diffCategories = (d.changes || []).map((e: any) => e.category ?? 'null');
        return diffCategories.some(category => selectedEventTypes.includes(category));
      });
    }

    // Apply tag filter
    if (selectedTags.length > 0) {
      filtered = filtered.filter(d => {
        // Check if any event in this diff has at least one of the selected tags
        return (d.changes || []).some((e: any) => {
          const eventTags = e.tags || [];
          return eventTags.some((tag: string) => selectedTags.includes(tag));
        });
      });
    }

    // Apply sorting for diffs view
    filtered.sort((a, b) => {
      if (diffSort === 'company') {
        // Sort by company slug alphabetically
        return a.slug.localeCompare(b.slug);
      } else if (diffSort === 'updated') {
        // Sort by updated date (newest first)
        const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
        const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
        return dateB - dateA;
      } else if (diffSort === 'version') {
        // Sort by period (2025Q4 > 2025Q1 > 2024Q3) - newest first
        const pA = a.period || '';
        const pB = b.period || '';
        if (!pA && !pB) return 0;
        if (!pA) return 1;
        if (!pB) return -1;
        // Simple string comparison works for YYYYQN format (descending)
        return pB.localeCompare(pA);
      } else {
        // Sort by created date (newest first)
        const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
        const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
        return dateB - dateA;
      }
    });

    return filtered;
  }, [diffs, activeSlugFilter, publishedFilter, createdFilter, selectedEventTypes, selectedTags, diffSort]);

  // Create all enriched events for company view navigation
  const allEnrichedEvents = React.useMemo(() => {
    return filteredDiffs.flatMap(diff =>
      (diff.changes || [])
        .filter(event => {
          // Apply category filter
          if (selectedEventTypes.length === 0) return true;
          const category = event.category ?? 'null';
          return selectedEventTypes.includes(category);
        })
        .map(event => ({
          ...event,
          diff_id: diff.id,
          // v_before/v_after, image URLs already on event from view
          // Add company logo from parent diff
          logo_url: diff.company_logo_url,
        }))
    );
  }, [filteredDiffs, selectedEventTypes]);

  // Use server-side tag counts (fetched in loadDiffs)
  // This provides accurate counts for the entire dataset, not just loaded events
  const tagCounts = serverTagCounts;

  // No client-side pagination needed - using server-side pagination with Load More button
  const paginatedDiffs = filteredDiffs;

  // Scroll to hash anchor when navigating back from detail page
  useEffect(() => {
    if (window.location.hash && !loading) {
      const element = document.querySelector(window.location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
    }
  }, [window.location.hash, loading]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <p className="text-gray-600 text-lg">Loading diffs{periodFilter ? ` for ${periodFilter}` : ''}...</p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-6">
        {/* Filters and Metrics Row */}
        <div className="space-y-4">
          {/* View Mode Toggle and Sort Controls */}
          <div className="flex items-center justify-between mb-4">
            {/* View Mode Toggle */}
            <div className="inline-flex items-center rounded-lg border border-gray-300 bg-white p-1">
              <button
                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  viewMode === 'company'
                    ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => {
                  setViewMode('company');
                  updateUrlParams({ view: 'company' });
                }}
                title="Group by Company"
              >
                <Building2 className="h-4 w-4" />
                Diffs ({stats?.totalDiffs || filteredDiffs.length})
              </button>
              <button
                className={`inline-flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-all ${
                  viewMode === 'event'
                    ? 'bg-blue-600 text-white shadow-sm hover:bg-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
                onClick={() => {
                  setViewMode('event');
                  updateUrlParams({ view: 'event' });
                }}
                title="List All Events"
              >
                <List className="h-4 w-4" />
                Events ({stats?.totalChanges || allEnrichedEvents.length})
              </button>
            </div>

            <div className="flex items-center gap-2">
              {/* Company Search */}
              <div className="flex items-center gap-1">
                <Input
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && slugInput.trim()) {
                      // Switch to all periods when searching
                      onPeriodChange?.('');
                      handleSearch();
                    }
                  }}
                  placeholder="Company..."
                  className="w-[140px] h-8"
                />
                <Button
                  onClick={() => {
                    if (slugInput.trim()) {
                      // Switch to all periods when searching
                      onPeriodChange?.('');
                      handleSearch();
                    }
                  }}
                  variant="outline"
                  size="sm"
                  className="h-8 w-8 p-0"
                  title="Search company"
                  disabled={!slugInput.trim()}
                >
                  <Search className="h-4 w-4" />
                </Button>
                {activeSlugFilter && (
                  <Button
                    onClick={() => {
                      setSlugInput('');
                      setActiveSlugFilter('');
                      updateUrlParams({ slug: null });
                      loadDiffs(1, true);
                    }}
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    title="Clear company filter"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Sort Selector - Show based on view mode */}
              {viewMode === 'company' ? (
                <Select
                  value={diffSort}
                  onValueChange={(value: 'company' | 'created' | 'updated' | 'version') => {
                    setDiffSort(value);
                    updateUrlParams({ diffSort: value });
                  }}
                >
                  <SelectTrigger className="w-[140px] h-8">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="updated">Sort: Updated</SelectItem>
                    <SelectItem value="created">Sort: Created</SelectItem>
                    <SelectItem value="version">Sort: Version</SelectItem>
                    <SelectItem value="company">Sort: Company</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <>
                  <Select
                    value={eventSort}
                    onValueChange={(value: 'type' | 'company' | 'created') => {
                      setEventSort(value);
                      updateUrlParams({ sort: value });
                    }}
                  >
                    <SelectTrigger className="w-[140px] h-8">
                      <SelectValue placeholder="Sort by..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="type">Sort: Type</SelectItem>
                      <SelectItem value="company">Sort: Company</SelectItem>
                      <SelectItem value="created">Sort: Created</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCSV}
                    className="h-8"
                    title="Export events to TSV"
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Export TSV
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="text-sm text-gray-600"
              >
                <ChevronDown className={`h-4 w-4 mr-1 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                Advanced Filters
              </Button>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="flex items-center gap-2">
            {statsLoading || !stats ? (
              <div className="flex items-center gap-2">
                <div className="h-6 w-16 bg-gray-200 animate-pulse rounded" />
                <div className="h-6 w-16 bg-gray-200 animate-pulse rounded" />
                <div className="h-6 w-16 bg-gray-200 animate-pulse rounded" />
              </div>
            ) : (
              <>
                {Object.entries(stats.eventTypeCounts || {})
                  .sort(([typeA], [typeB]) => {
                    // First sort by priority order
                    const priorityA = getCategorySortPriority(typeA);
                    const priorityB = getCategorySortPriority(typeB);
                    if (priorityA !== priorityB) {
                      return priorityA - priorityB;
                    }
                    // Then alphabetically for items with same priority
                    return typeA.localeCompare(typeB);
                  })
                  .map(([type, count]) => {
                    const isSelected = selectedEventTypes.length === 0 || selectedEventTypes.includes(type);
                    const baseColor = getCategoryColor(type);
                    return (
                      <Badge
                        key={type}
                        variant="outline"
                        className={`text-xs cursor-pointer transition-all ${
                          isSelected
                            ? `${baseColor} hover:opacity-80`
                            : 'bg-gray-50 text-gray-400 border-gray-200 hover:bg-gray-100 opacity-60'
                        }`}
                        onClick={() => toggleEventType(type)}
                      >
                        <span className="font-semibold">{count}</span>
                        <span className="ml-1">{formatCategoryLabel(type)}</span>
                      </Badge>
                    );
                  })}
                {selectedEventTypes.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 px-2 text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      setSelectedEventTypes([]);
                      updateUrlParams({ types: null });
                    }}
                  >
                    Clear
                  </Button>
                )}
              </>
            )}
          </div>

          {/* Tag Filter Bar Graph */}
          {tagCounts.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-gray-500 flex items-center gap-1">
                  <Tag className="h-3 w-3" />
                  Tags
                </span>
                {selectedTags.length > 0 && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-5 px-2 text-xs text-gray-500 hover:text-gray-700"
                    onClick={() => {
                      setSelectedTags([]);
                      updateUrlParams({ tags: null });
                    }}
                  >
                    Clear tags
                  </Button>
                )}
              </div>
              <div className="flex flex-wrap gap-1">
                {tagCounts.map(([tag, count]) => {
                  const isSelected = selectedTags.includes(tag);
                  const maxCount = tagCounts[0]?.[1] || 1;
                  const barWidth = Math.max(20, (count / maxCount) * 100);

                  // Format tag label: snake_case -> Title Case
                  const formatTagLabel = (t: string) =>
                    t.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

                  return (
                    <button
                      key={tag}
                      onClick={() => toggleTag(tag)}
                      className={`group relative flex items-center h-6 text-xs rounded transition-all ${
                        isSelected
                          ? 'bg-blue-100 text-blue-800 ring-1 ring-blue-300'
                          : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                      }`}
                      title={`${formatTagLabel(tag)}: ${count} events`}
                    >
                      {/* Bar background */}
                      <div
                        className={`absolute left-0 top-0 h-full rounded transition-all ${
                          isSelected ? 'bg-blue-200' : 'bg-gray-100 group-hover:bg-gray-200'
                        }`}
                        style={{ width: `${barWidth}%` }}
                      />
                      {/* Label */}
                      <span className="relative px-2 whitespace-nowrap flex items-center gap-1">
                        <span className="font-medium">{count}</span>
                        <span className="truncate max-w-[100px]">{formatTagLabel(tag)}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Advanced Filters */}
          {showAdvanced && (
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-end gap-3">
                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-700">Created</span>
                    <Select
                      value={createdFilter}
                      onValueChange={(value) => {
                        setCreatedFilter(value);
                        updateUrlParams({ created: value });
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Select range" />
                      </SelectTrigger>
                      <SelectContent>
                        {createdRangeOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-gray-700">Published</span>
                    <Select
                      value={publishedFilter}
                      onValueChange={(value) => {
                        setPublishedFilter(value);
                        updateUrlParams({ published: value });
                      }}
                    >
                      <SelectTrigger className="w-[130px]">
                        <SelectValue placeholder="Select published" />
                      </SelectTrigger>
                      <SelectContent>
                        {publishedStatuses.map((status) => (
                          <SelectItem key={status.value} value={status.value}>
                            {status.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Diffs List */}
        <div className="space-y-4">
          {filteredDiffs.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-gray-500">
                No diffs found{periodFilter && ` for ${periodFilter}`}
              </CardContent>
            </Card>
          ) : viewMode === 'company' ? (
            // Company view (default) - existing behavior with pagination
            paginatedDiffs.map((diff) => (
              <DiffCard
                key={diff.id}
                diff={diff}
                onOpenLightbox={handleOpenLightbox}
                onRefresh={() => {
                  loadDiffs(1, true);
                  refetchFixDiffTasks();
                }}
                onEdit={() => {
                  // Store the ordered list of diff IDs for navigation in the edit page
                  dispatch(setNavigationList(filteredDiffs.map(d => d.id)));
                }}
                selectedEventTypes={selectedEventTypes}
                allEnrichedEvents={allEnrichedEvents}
                fixDiffTask={tasksByNewsId.get(diff.id) || null}
              />
            ))
          ) : (
            // Event view - flat list of all events with sorting
            (() => {
              // Flatten events and attach parent diff data (including image URLs)
              const allEvents = filteredDiffs.flatMap(diff =>
                (diff.changes || [])
                  .filter(event => {
                    // Filter events by selected categories
                    if (selectedEventTypes.length === 0) return true;
                    const category = event.category ?? 'null';
                    return selectedEventTypes.includes(category);
                  })
                  .map(event => ({
                    ...event,
                    diff_id: diff.id,
                    slug: diff.slug,
                    // Don't override event's URLs - they're already correct from the view
                    // Each event has its own asset_id with its own URLs
                    logo_url: diff.company_logo_url,
                    diff_created_at: diff.created_at,
                  }))
              );

              // Apply sorting based on eventSort state
              const sortedEvents = [...allEvents].sort((a, b) => {
                if (eventSort === 'type') {
                  // Sort by category alphabetically
                  const categoryA = a.category ?? 'null';
                  const categoryB = b.category ?? 'null';

                  // If different categories, sort alphabetically
                  if (categoryA !== categoryB) {
                    return categoryA.localeCompare(categoryB);
                  }

                  // If same category, sort by company
                  return a.slug.localeCompare(b.slug);
                } else if (eventSort === 'company') {
                  // Sort by company (slug) first, then by category
                  if (a.slug !== b.slug) {
                    return a.slug.localeCompare(b.slug);
                  }
                  const categoryA = a.category ?? 'null';
                  const categoryB = b.category ?? 'null';
                  return categoryA.localeCompare(categoryB);
                } else {
                  // Sort by created date (newest first)
                  const dateA = a.diff_created_at ? new Date(a.diff_created_at).getTime() : 0;
                  const dateB = b.diff_created_at ? new Date(b.diff_created_at).getTime() : 0;
                  return dateB - dateA;
                }
              });

              return sortedEvents.length > 0 ? (
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                      <List className="h-5 w-5" />
                      All Events ({sortedEvents.length})
                    </h3>
                    <div className="space-y-2">
                      {sortedEvents.map(event => (
                        <div
                          key={event.id}
                          className="p-3 hover:bg-gray-50 rounded cursor-pointer flex items-start justify-between gap-3"
                          onClick={() => handleOpenLightbox(event.diff_id, event.id, event.slug, sortedEvents)}
                        >
                          <div className="flex items-start gap-3 flex-1 min-w-0">
                            {event.logo_url && (
                              <img
                                src={event.logo_url}
                                alt={`${event.slug} logo`}
                                className="h-8 w-8 rounded object-contain flex-shrink-0 bg-white border border-gray-200"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-gray-900">{event.slug}</span>
                                <span className={`px-2 py-0.5 text-xs rounded border ${getCategoryColor(event.category)}`}>
                                  {formatCategoryLabel(event.category)}
                                </span>
                                {event.diff_created_at && (
                                  <span className="text-xs text-gray-500">
                                    {formatDistanceToNow(new Date(event.diff_created_at), { addSuffix: true })}
                                  </span>
                                )}
                              </div>
                              {event.description && (
                                <div className="text-sm text-gray-600 mt-1 line-clamp-2">{event.description}</div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="p-8 text-center text-gray-500">
                    No events found
                  </CardContent>
                </Card>
              );
            })()
          )}
        </div>

        {/* Load More Button */}
        {hasMoreEvents && !loading && (
          <div className="flex items-center justify-center py-6">
            <Button
              variant="outline"
              size="lg"
              onClick={loadMoreEvents}
              disabled={loadingMore}
              className="min-w-[200px]"
            >
              {loadingMore ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-900 mr-2" />
                  Loading...
                </>
              ) : (
                `Load More Events (${diffs.reduce((sum, d) => sum + (d.changes?.length || 0), 0)} loaded)`
              )}
            </Button>
          </div>
        )}

        {/* Show message when all events loaded */}
        {!hasMoreEvents && !loading && diffs.length > 0 && (
          <div className="text-center py-6 text-sm text-gray-500">
            All events loaded ({diffs.reduce((sum, d) => sum + (d.changes?.length || 0), 0)} total)
          </div>
        )}
      </div>

      {/* Event Lightbox */}
      {lightboxContext && (
        <DiffEventLightbox
          isOpen={lightboxOpen}
          onClose={() => {
            setLightboxOpen(false);
            // Clear eventId from URL when closing lightbox
            updateUrlParams({ eventId: null });
          }}
          diffId={lightboxContext.diffId}
          initialEventId={lightboxContext.eventId}
          companySlug={lightboxContext.companySlug}
          events={lightboxContext.events}
        />
      )}
    </>
  );
};
